import { useState } from "react";
import { Sparkles, Loader2, Copy, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { OUTPUT_MODES, LANGUAGES } from "@/lib/brand";

const SAMPLES = [
  { id: "hinglish-email", lang: "mixed", mode: "email", label: "Hinglish · Email to client",
    text: "Hi Rohan boss yaar, just wanted to update you ki the new dashboard design is almost ready, maybe 2 din mein we will ship the prototype, and once you review it then we can do the final QA before launch, please let me know if any thoughts." },
  { id: "english-whatsapp", lang: "en", mode: "whatsapp", label: "English · WhatsApp reply",
    text: "Hey thanks so much for the detailed feedback uh I will definitely incorporate the changes in the next iteration and send you the updated version by end of day tomorrow let me know if that timing works for you." },
  { id: "english-meeting", lang: "en", mode: "meeting_note", label: "English · Meeting notes",
    text: "So we met today with the marketing team ankit said launch is on 15 march budget is two lakh and we need landing page copy by next monday, sneha will write the social posts and i will brief the agency about creatives, follow up is next thursday." },
];

export default function LiveDemo() {
  const [sample, setSample] = useState(SAMPLES[0]);
  const [text, setText] = useState(SAMPLES[0].text);
  const [mode, setMode] = useState(SAMPLES[0].mode);
  const [lang, setLang] = useState(SAMPLES[0].lang);
  const [polished, setPolished] = useState("");
  const [busy, setBusy] = useState(false);

  const pick = (s) => { setSample(s); setText(s.text); setMode(s.mode); setLang(s.lang); setPolished(""); };

  const run = async () => {
    if (!text.trim()) { toast.error("Type or pick a sample first."); return; }
    setBusy(true); setPolished("");
    try {
      const { data } = await api.post("/demo/polish", { raw_text: text, output_mode: mode, language: lang });
      setPolished(data.polished_text);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Demo failed. Please sign up to continue.");
    } finally { setBusy(false); }
  };

  const copy = async () => { await navigator.clipboard.writeText(polished); toast.success("Copied"); };

  return (
    <section id="live-demo" className="py-24 bg-white border-y border-brand-borderc">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">Live demo · No signup</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">Try DraftVoice right here.</h2>
          <p className="mt-3 text-brand-muted">Pick a sample transcript (or type your own messy thought) and watch it become a polished draft.</p>
        </div>

        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          {/* Input */}
          <div className="bg-brand-bg rounded-3xl border border-brand-borderc p-6">
            <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">1 · Pick a sample</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {SAMPLES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`demo-sample-${s.id}`}
                  onClick={() => pick(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    sample.id === s.id ? "bg-gradient-primary text-white border-transparent" : "bg-white border-brand-borderc text-brand-text hover:bg-brand-surface"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <div className="mt-5 text-xs font-semibold uppercase tracking-wider text-brand-muted">2 · Your raw transcript</div>
            <textarea
              data-testid="demo-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={7}
              maxLength={1200}
              className="mt-2 w-full p-3 rounded-xl border border-brand-borderc bg-white text-sm focus:border-brand-purple focus:ring-2 focus:ring-brand-purple/20 outline-none resize-none"
            />
            <div className="text-[11px] text-brand-muted mt-1">Demo limited to ~120 words.</div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted mb-2">Language</div>
                <select data-testid="demo-language" value={lang} onChange={(e) => setLang(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-brand-borderc bg-white text-sm focus:border-brand-purple outline-none">
                  {LANGUAGES.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted mb-2">Output mode</div>
                <select data-testid="demo-mode" value={mode} onChange={(e) => setMode(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-brand-borderc bg-white text-sm focus:border-brand-purple outline-none">
                  {OUTPUT_MODES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                </select>
              </div>
            </div>

            <button
              data-testid="demo-polish-btn"
              onClick={run} disabled={busy}
              className="mt-5 w-full btn-primary-gradient rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Polishing…</> : <><Sparkles className="w-4 h-4" /> Polish with AI</>}
            </button>
          </div>

          {/* Output */}
          <div className="bg-white rounded-3xl border border-brand-borderc p-6 relative overflow-hidden">
            <div className="absolute inset-0 bg-mesh opacity-30 pointer-events-none" />
            <div className="relative">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-wider text-brand-muted">3 · AI polished output</div>
                {polished && (
                  <button data-testid="demo-copy-btn" onClick={copy} className="text-xs px-3 py-1.5 rounded-full bg-brand-surface hover:bg-brand-borderc inline-flex items-center gap-1"><Copy className="w-3.5 h-3.5" /> Copy</button>
                )}
              </div>

              <div data-testid="demo-output" className="mt-3 min-h-[280px] p-4 rounded-xl border border-brand-borderc bg-brand-bg whitespace-pre-wrap text-sm text-brand-text leading-relaxed">
                {busy ? (
                  <div className="h-full flex flex-col items-center justify-center text-brand-muted py-12">
                    <Loader2 className="w-8 h-8 text-brand-purple animate-spin" />
                    <div className="mt-3 text-sm">AI is polishing your draft…</div>
                  </div>
                ) : polished ? polished : (
                  <div className="h-full flex flex-col items-center justify-center text-brand-muted py-12">
                    <Sparkles className="w-8 h-8 text-brand-purple" />
                    <div className="mt-3 text-sm">Tap "Polish with AI" to see the magic.</div>
                  </div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-brand-muted">Liked it? Get unlimited drafting + voice input.</div>
                <Link to="/signup" data-testid="demo-signup-cta" className="btn-primary-gradient rounded-full px-5 py-2 text-sm font-semibold inline-flex items-center gap-1">
                  Start free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
